import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../apiBase";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatMonthLabel(year, month) {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function formatDateLabel(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function starsString(n) {
  if (n === 3) return "★★★";
  if (n === 2) return "★★☆";
  return "★☆☆";
}

function formatValue(v) {
  if (v === null || v === undefined) return "—";
  if (Math.abs(v) > 1000) return v.toLocaleString();
  return v.toString();
}

export default function MacroEventsSection() {
  const today = new Date();
  const [year] = useState(today.getFullYear());
  const [month] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [impactFilter, setImpactFilter] = useState("all");
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthLabel = useMemo(() => formatMonthLabel(year, month), [year, month]);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const resp = await fetch(
          apiUrl(`/api/events/month?year=${year}&month=${pad2(month)}`)
        );
        const data = await resp.json();
        setEvents(data.events || []);
        setAsOf(new Date());
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [year, month]);

  // If you still want the Refresh button, keep a separate handler:
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        apiUrl(`/api/events/month?year=${year}&month=${pad2(month)}`)
      );
      const data = await resp.json();
      setEvents(data.events || []);
      setAsOf(new Date());
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (impactFilter === "all") return events;
    if (impactFilter === "3") return events.filter((e) => e.impactStars === 3);
    if (impactFilter === "2+") return events.filter((e) => e.impactStars >= 2);
    if (impactFilter === "1+") return events.filter((e) => e.impactStars >= 1);
    return events;
  }, [events, impactFilter]);

  return (
    <section className="section-card macro-events-section">
      {/* HERO */}
      <div className="market-tone">
        <div>
          <h2>Macro Events</h2>
          <p>
            {monthLabel} (US Releases)
            {asOf && (
              <span className="asof-inline">
                {" "}
                · As of {asOf.toLocaleString()}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* CONTROL BAR */}
      <div className="macro-control-wrapper">
        <div className="macro-control-left">
          <span className="month-label">{monthLabel}</span>
        </div>

        <div className="macro-control-right">
          <button
            className={impactFilter === "all" ? "seg active" : "seg"}
            onClick={() => setImpactFilter("all")}
          >
            All
          </button>

          <button
            className={impactFilter === "3" ? "seg active" : "seg"}
            onClick={() => setImpactFilter("3")}
          >
            ★★★
          </button>

          <button
            className={impactFilter === "2+" ? "seg active" : "seg"}
            onClick={() => setImpactFilter("2+")}
          >
            ★★☆+
          </button>

          <button
            className={impactFilter === "1+" ? "seg active" : "seg"}
            onClick={() => setImpactFilter("1+")}
          >
            ★☆☆+
          </button>

          <button className="seg subtle" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="macro-table-card">
        {loading ? (
          <p className="muted">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="muted">No events found.</p>
        ) : (
          <table className="macro-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Impact</th>
                <th>Event</th>
                <th>Actual</th>
                <th>Previous</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td>{formatDateLabel(e.date)}</td>
                  <td className="impact-cell">{starsString(e.impactStars)}</td>
                  <td>
                    <div className="event-name">{e.event}</div>
                    <div className="event-time">{e.time} ET</div>
                  </td>
                  <td>{formatValue(e.actual)}</td>
                  <td>{formatValue(e.previous)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* HOW TO READ */}
      <div className="info-card">
        <h4>How to read this</h4>
        <p>★★★ = Major market-moving releases</p>
        <p>★★☆ = Important macro indicators</p>
        <p>★☆☆ = Secondary / regional data</p>
        <p>Compare Actual vs Previous to gauge momentum.</p>
      </div>
    </section>
  );
}